import { z } from "zod";
import { MEETING_NOTICE_KINDS } from "./medical-notice";

// TES-1393 (2.1 S5) — the doctor's messages about care meetings, from 09C
// "KANONISKT TILLÄGG 2026-10-13 — MEDDELANDE FRÅN DIN LÄKARE" and Ö-561
// (TES-1362): "Om ditt vårdmöte och Förslag om extra vårdmöte ska visas
// under Din vård → Vårdmöten, inte som vårdanteckningar."
//
// Both are published through the doctor's "Skicka besked" (POST
// …/doctor-notices with `type`), signed and versioned like every notice.
// A build before CLIENT_CONTRACT_CHANGES.meetingNotices reads them as a
// `doctor_notice`, exactly as today.
//
// "Förslag om extra vårdmöte" does not create a MeetingOffer (TES-1168): an
// offer is a held time with a provider, a length and a price, and the
// proposal has none of those; it recommends contact, it does not book it.
// Its traceable task is the proposal itself, open until a meeting she books
// after it exists (09C: "Meddelandet får inte på egen hand markera att
// mötet är bokat"). When the doctor wants a specific time, "Boka nytt
// vårdmöte" (TES-1168) is the tool, with its own push and confirmation.

export const meetingNoticeKindSchema = z.enum(MEETING_NOTICE_KINDS);

// The composer's message types: the existing medical notice and the two
// meeting messages. `doctor_notice` is 09C's "Medicinskt besked".
export const DOCTOR_MESSAGE_TYPES = [
  "doctor_notice",
  ...MEETING_NOTICE_KINDS,
] as const;
export const doctorMessageTypeSchema = z.enum(DOCTOR_MESSAGE_TYPES);
export type DoctorMessageType = z.infer<typeof doctorMessageTypeSchema>;

// 09C's CTAs for "Om ditt vårdmöte": "Möjliga CTA:er: Bekräfta ny tid, Välj
// en annan tid eller Boka vårdmöte." "Förslag om extra vårdmöte" always
// carries `book_meeting`.
//   confirm_new_time  — the time the clinic proposed (her open MeetingOffer);
//                       refused at publish when she has none, so the CTA is
//                       always a real one;
//   choose_other_time — her booking, under the existing booking rules;
//   book_meeting      — her booking, under the existing booking rules
//                       (self-book or Kontakta Ella, unchanged).
export const MEETING_NOTICE_CTAS = [
  "confirm_new_time",
  "choose_other_time",
  "book_meeting",
] as const;
export const meetingNoticeCtaSchema = z.enum(MEETING_NOTICE_CTAS);
export type MeetingNoticeCta = z.infer<typeof meetingNoticeCtaSchema>;

// Verbatim from 09C 2026-10-13 unless marked otherwise.
export const MEETING_NOTICE_COPY = {
  typeLabel: {
    // 09C's third type, "Medicinskt besked".
    doctor_notice: "Medicinskt besked",
    meeting_info: "Om ditt vårdmöte",
    meeting_proposal: "Förslag om extra vårdmöte",
  } satisfies Record<DoctorMessageType, string>,
  cta: {
    confirm_new_time: "Bekräfta ny tid",
    choose_other_time: "Välj en annan tid",
    book_meeting: "Boka vårdmöte",
  } satisfies Record<MeetingNoticeCta, string>,
  // "Förinställd mötestext vid tekniskt fel".
  technicalFaultPreset:
    "Vi fick tyvärr tekniska problem vid ditt vårdmöte och kunde inte genomföra det som planerat. Jag beklagar det. Vi kontaktar dig för att hitta en ny tid. Du behöver inte boka eller betala ett nytt möte.",
  // The function's name and its push: "Patienten får pushnotis: ”Du har
  // fått ett nytt meddelande från din läkare.”"
  pushTitle: "Meddelande från din läkare",
  pushBody: "Du har fått ett nytt meddelande från din läkare.",
  // Ö-618 (Emelie 2026-10-20), verbatim: the Home task while a proposal has
  // no meeting booked after it. It opens the proposal (medicalNoticeDeepLink),
  // where "Boka vårdmöte" is. Its push is the general doctor push above.
  proposalTask: {
    title: "Din läkare föreslår ett extra vårdmöte",
    body: "Läs förslaget och boka en tid som passar dig.",
    ctaLabel: "Läs förslaget",
  },
} as const;

// What a meeting message carries beyond the notice, for a build at or after
// CLIENT_CONTRACT_CHANGES.meetingNotices.
//   cta             — the doctor's CTA, null for none;
//   offerId         — confirm_new_time: her open MeetingOffer, the time to
//                     confirm; null once it is no longer open (the app
//                     hides the CTA then);
//   bookedMeetingId — meeting_proposal: the meeting she booked after it,
//                     null while the task is open. Always null on
//                     meeting_info.
export const patientMeetingNoticeDetailSchema = z.object({
  cta: meetingNoticeCtaSchema.nullable(),
  offerId: z.string().nullable(),
  bookedMeetingId: z.string().nullable(),
});
export type PatientMeetingNoticeDetail = z.infer<
  typeof patientMeetingNoticeDetailSchema
>;

// GET patient-api/v1/din-vard/meeting-messages — the messages for Din vård
// → "Vårdmöten", newest first, the newest version of each. Opening one is the
// notice's own detail and read routes (…/medical-notices/:id).
export const patientMeetingMessageSchema = z.object({
  noticeId: z.string(),
  kind: meetingNoticeKindSchema,
  label: z.string(),
  body: z.string(),
  publishedAt: z.string(),
  publishedBy: z.string().nullable(),
  read: z.boolean(),
  meeting: patientMeetingNoticeDetailSchema,
});
export type PatientMeetingMessage = z.infer<typeof patientMeetingMessageSchema>;

export const patientMeetingMessagesResponseSchema = z.object({
  messages: z.array(patientMeetingMessageSchema),
});
export type PatientMeetingMessagesResponse = z.infer<
  typeof patientMeetingMessagesResponseSchema
>;
